import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, Badge, Modal } from 'react-bootstrap';

// URL base del backend
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

const MessageTemplates = ({ onSuccess }) => {
  // =====================================================
  // ESTADO
  // =====================================================
  const [plantillas, setPlantillas] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroCanal, setFiltroCanal] = useState('todos');
  const [editando, setEditando] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [preview, setPreview] = useState(null);

  // Estado del formulario de edición
  const [formData, setFormData] = useState({
    asunto: '',
    contenido: '',
    contenidoHTML: ''
  });

  // =====================================================
  // CARGAR DATOS
  // =====================================================
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const correo = localStorage.getItem('correo');
      const headers = {
        'Content-Type': 'application/json',
        'usuario-correo': correo || ''
      };
      
      const [plantillasRes, variablesRes] = await Promise.all([
        fetch(`${API_BASE}/api/config/plantillas-mensajes`, { headers }),
        fetch(`${API_BASE}/api/config/plantillas-mensajes/variables`, { headers })
      ]);

      const plantillasData = await plantillasRes.json();
      const variablesData = await variablesRes.json();
      
      if (plantillasData.success) {
        setPlantillas(plantillasData.data);
      }
      
      if (variablesData.success) {
        setVariables(variablesData.data?.lista || []);
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // HANDLERS
  // =====================================================
  const handleEditar = (plantilla) => {
    setFormData({
      asunto: plantilla.asunto || '',
      contenido: plantilla.contenido || '',
      contenidoHTML: plantilla.contenidoHTML || ''
    });
    setEditando(plantilla);
    setShowModal(true);
    setPreview(null);
  };

  const handleCerrarModal = () => {
    setShowModal(false);
    setEditando(null);
    setFormData({ asunto: '', contenido: '', contenidoHTML: '' });
    setPreview(null);
  };

  const insertarVariable = (variable) => {
    const tag = `{{${variable}}}`;
    setFormData(prev => ({
      ...prev,
      contenido: prev.contenido + tag
    }));
  };

  // =====================================================
  // GUARDAR PLANTILLA
  // =====================================================
  const handleGuardar = async () => {
    try {
      setGuardando(true);
      setError(null);

      const correo = localStorage.getItem('correo');

      const response = await fetch(`${API_BASE}/api/config/plantillas-mensajes/${editando.codigo}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        onSuccess?.('Plantilla actualizada correctamente');
        handleCerrarModal();
        cargarDatos();
      } else {
        setError(data.error || 'Error al guardar');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setGuardando(false);
    }
  };

  // =====================================================
  // PREVIEW DE PLANTILLA
  // =====================================================
  const handlePreview = async () => {
    try {
      const correo = localStorage.getItem('correo');

      const response = await fetch(`${API_BASE}/api/config/plantillas-mensajes/${editando.codigo}/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (data.success) {
        setPreview(data.data.procesado);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // =====================================================
  // FILTRAR PLANTILLAS
  // =====================================================
  const plantillasFiltradas = filtroCanal === 'todos' 
    ? plantillas 
    : plantillas.filter(p => p.canal === filtroCanal);

  // =====================================================
  // BADGE COLOR POR CANAL
  // =====================================================
  const getBadgeVariant = (canal) => {
    switch(canal) {
      case 'EMAIL': return 'primary';
      case 'SMS': return 'success';
      case 'WHATSAPP': return 'info';
      default: return 'secondary';
    }
  };

  // =====================================================
  // RENDER - LOADING
  // =====================================================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Cargando plantillas...</span>
      </div>
    );
  }

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1">
          <i className="bi bi-chat-dots text-primary me-2"></i>
          Plantillas de Mensajes
        </h5>
        <small className="text-muted">Personaliza los mensajes que el sistema envía</small>
      </div>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <div className="mb-3 d-flex flex-wrap gap-2">
        {['todos', 'EMAIL', 'SMS', 'WHATSAPP'].map(canal => (
          <Badge 
            key={canal}
            bg={filtroCanal === canal ? 'primary' : 'secondary'}
            style={{ cursor: 'pointer' }}
            onClick={() => setFiltroCanal(canal)}
            className="py-2 px-3"
          >
            {canal === 'todos' ? 'Todos' : canal}
          </Badge>
        ))}
      </div>

      {/* Lista de Plantillas */}
      <Row>
        {plantillasFiltradas.map((plantilla) => (
          <Col md={6} key={plantilla._id} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h6 className="mb-1">{plantilla.nombre}</h6>
                    <Badge bg="secondary" className="me-1">{plantilla.codigo}</Badge>
                    <Badge bg={getBadgeVariant(plantilla.canal)}>{plantilla.canal}</Badge>
                  </div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleEditar(plantilla)}
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Editar
                  </Button>
                </div>
                <p className="text-muted small mb-0">{plantilla.descripcion}</p>
              </Card.Body>
            </Card>
          </Col>
        ))}

        {plantillasFiltradas.length === 0 && (
          <Col>
            <div className="text-center py-5 text-muted">
              <i className="bi bi-chat-dots fs-1 d-block mb-3"></i>
              <p>No hay plantillas para este filtro</p>
            </div>
          </Col>
        )}
      </Row>

      {/* Modal de Edición */}
      <Modal show={showModal} onHide={handleCerrarModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Editar Plantilla: {editando?.nombre}
            <Badge bg={getBadgeVariant(editando?.canal)} className="ms-2">
              {editando?.canal}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Variables disponibles */}
          <Alert variant="info" className="mb-3">
            <strong><i className="bi bi-code me-2"></i>Variables disponibles:</strong>
            <div className="mt-2 d-flex flex-wrap gap-1">
              {variables.map(v => (
                <Badge 
                  key={v.codigo}
                  bg="light" 
                  text="dark"
                  style={{ cursor: 'pointer' }}
                  onClick={() => insertarVariable(v.codigo)}
                  title={v.descripcion}
                >
                  {`{{${v.codigo}}}`}
                </Badge>
              ))}
            </div>
          </Alert>

          {/* Asunto (solo para EMAIL) */}
          {editando?.canal === 'EMAIL' && (
            <Form.Group className="mb-3">
              <Form.Label>Asunto del correo</Form.Label>
              <Form.Control
                type="text"
                value={formData.asunto}
                onChange={(e) => setFormData(prev => ({ ...prev, asunto: e.target.value }))}
                placeholder="Ej: 📄 Documento pendiente de firma"
              />
            </Form.Group>
          )}

          {/* Contenido */}
          <Form.Group className="mb-3">
            <Form.Label>Contenido del mensaje</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={formData.contenido}
              onChange={(e) => setFormData(prev => ({ ...prev, contenido: e.target.value }))}
              placeholder="Escribe el mensaje aquí..."
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Group>

          {/* Preview */}
          {preview && (
            <div className="bg-light rounded p-3">
              <h6 className="mb-2">
                <i className="bi bi-eye me-2"></i>Vista previa:
              </h6>
              {preview.asunto && (
                <p className="mb-2"><strong>Asunto:</strong> {preview.asunto}</p>
              )}
              <pre className="mb-0 bg-white p-2 rounded" style={{ whiteSpace: 'pre-wrap' }}>
                {preview.contenido}
              </pre>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={handlePreview}>
            <i className="bi bi-eye me-2"></i>
            Vista previa
          </Button>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={handleCerrarModal}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleGuardar} disabled={guardando}>
              {guardando ? (
                <><Spinner size="sm" className="me-2" />Guardando...</>
              ) : (
                <><i className="bi bi-check-lg me-2"></i>Guardar</>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MessageTemplates;
